// Twin Bore point-blank blast (owner 2026-11-02: 'a short range, wide blast… uses both hands… a quick blast in front of
// him'). Render-only reaction to the sim's 'boreBlast' event: a bright cel fan of energy that bursts from his hands and
// fades in ~0.18 s (white-hot near the palms → gold → orange edge, ragged front), pellet streaks racing through the cone,
// a muzzle flash, and sparks / small pops on every foe the blast hit.
import * as THREE from 'three/webgpu';
import { color, float, mix, smoothstep, uniform, uv, mx_noise_float, vec2, vec3 } from 'three/tsl';
import type { Fx } from './fx';
import { FLASH } from './fx';

const LIFE = 0.22, POOL = 4;
interface Slot { mesh: THREE.Mesh; t: number; age: ReturnType<typeof uniform> }

export class BoreBlast {
  private slots: Slot[] = [];
  constructor(scene: THREE.Scene) {
    for (let k = 0; k < POOL; k++) {
      const age = uniform(1);
      const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false }); // normal blending: additive washed out on bright grass
      // geometry uv: x = 0..1 across the arc, y = 0..1 from the palms (inner) to the reach (outer)
      const r = uv().y, a = uv().x.sub(0.5).abs().mul(2);
      const n = mx_noise_float(vec3(uv().x.mul(9), r.mul(3).sub(age.mul(6)), float(k))).mul(0.5).add(0.5);
      const front = age.mul(1.6).min(1);                                // the wave front races out in ~0.11 s
      const body = smoothstep(front.add(0.02), front.sub(0.1).add(n.mul(0.12)), r);          // ragged leading edge
      const sides = smoothstep(1.0, 0.75, a.add(n.mul(0.15)));
      const fade = float(1).sub(age).max(0).pow(1.4);
      const heat = float(1).sub(r).mul(0.8).add(n.mul(0.3));
      let col = mix(color(0xff7a20), color(0xffd050), smoothstep(0.25, 0.45, heat));
      col = mix(col, color(0xfff6e0), smoothstep(0.62, 0.72, heat));
      m.colorNode = col.mul(1.15);
      m.opacityNode = body.mul(sides).mul(fade).mul(smoothstep(0.0, 0.08, r)).mul(1.0).min(0.95);
      void vec2;
      const geo = new THREE.RingGeometry(0.35, 1, 24, 6, 0, 1); // re-made per blast with the right arc
      const mesh = new THREE.Mesh(geo, m); mesh.visible = false; mesh.frustumCulled = false; mesh.renderOrder = 3;
      scene.add(mesh);
      this.slots.push({ mesh, t: 9, age });
    }
  }

  fire(x: number, gy: number, z: number, dx: number, dz: number, r: number, half: number, n: number, hits: number[], evolved: boolean, fx: Fx): void {
    const s = this.slots.reduce((a, b) => (b.t > a.t ? b : a));
    s.t = 0;
    const g = new THREE.RingGeometry(0.45, r, 28, 6, -half, half * 2);
    // RingGeometry uv is planar; rebuild it as (arc fraction, radial fraction) for the shader
    const pos = g.attributes.position as THREE.BufferAttribute, uvA = g.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const px = pos.getX(i), py = pos.getY(i), ang = Math.atan2(py, px), rad = Math.hypot(px, py);
      uvA.setXY(i, (ang + half) / (half * 2), (rad - 0.45) / Math.max(0.01, r - 0.45));
    }
    s.mesh.geometry.dispose(); s.mesh.geometry = g;
    s.mesh.position.set(x + dx * 0.4, gy + 1.0, z + dz * 0.4);
    s.mesh.rotation.set(-Math.PI / 2, 0, Math.atan2(dz, dx)); // lie flat at hand height, arc centred on the aim
    s.mesh.visible = true;
    // muzzle: both palms flare together
    const hx = x + dx * 0.7, hz = z + dz * 0.7;
    fx.stars.add(hx, gy + 1.1, hz, 1.8, 0.1, 0xfff0c0, 2.6);
    if (FLASH.dim >= 1) fx.lights.flash(hx, gy + 1.1, hz, 0xffc060, 18, 0.12, 9);
    // pellet streaks racing through the cone
    const np = Math.min(22, n + 6), base = Math.atan2(dz, dx);
    for (let k = 0; k < np; k++) {
      const a = base + (Math.random() * 2 - 1) * half, sp = r / 0.13 * (0.75 + Math.random() * 0.35);
      fx.particles.emitDir(hx, gy + 0.9 + Math.random() * 0.4, hz, Math.cos(a) * sp, (Math.random() - 0.5) * 2, Math.sin(a) * sp, k % 3 ? 0xffd070 : 0xffffff, 0.22, 0.13, 3);
    }
    // sparks on everything hit; evolved: a small pop on each
    for (let i = 0; i + 1 < hits.length; i += 2) {
      fx.stars.add(hits[i], gy + 1, hits[i + 1], 1.0, 0.08, 0xffe0a0, 2.4);
      if (evolved) fx.blast?.pop(hits[i], gy + 0.8, hits[i + 1], 0xffa040);
    }
    fx.shake = Math.max(fx.shake, 0.12);
  }

  update(dt: number): void {
    for (const s of this.slots) {
      if (s.t >= LIFE) { s.mesh.visible = false; continue; }
      s.t += dt; s.age.value = Math.min(1, s.t / LIFE);
    }
  }
  clear(): void { for (const s of this.slots) { s.t = 9; s.mesh.visible = false; } }
}
